import type { AgentTool, AgentToolInput, AgentToolResult } from './agent-tool';
import { maskDocuments, maskForAgent } from './screen-context';

export interface AgentToolAdapter {
  readonly id: 'webmcp' | 'in-memory';
  registerTool(tool: AgentTool): void;
  unregisterTool(name: string): void;
  registeredNames(): readonly string[];
}

export class AgentToolRegistrationError extends Error {
  readonly toolName: string;
  readonly code: string;

  constructor(toolName: string, code: string, detail: string) {
    super(`ferramenta ${toolName} recusada: ${detail}`);
    this.name = 'AgentToolRegistrationError';
    this.toolName = toolName;
    this.code = code;
  }
}

const TOOL_NAME = /^[A-Za-z0-9_-]+$/;

export function checkAgentTool(tool: AgentTool, registered: Iterable<string>): { code: string; detail: string } | null {
  if (!TOOL_NAME.test(tool.name)) return { code: 'invalid-name', detail: 'nome fora de [A-Za-z0-9_-]' };
  if (tool.description.trim() === '') return { code: 'empty-description', detail: 'descrição vazia' };
  if (tool.inputSchema['type'] !== 'object') return { code: 'invalid-input-schema', detail: 'inputSchema precisa ser type: object' };
  for (const name of registered) if (name === tool.name) return { code: 'duplicate-name', detail: 'nome já registrado' };
  if (!tool.readOnly && tool.preview === undefined) return { code: 'write-without-preview', detail: 'escrita sem prévia (U24)' };
  return null;
}

export interface InMemoryAgentToolAdapter extends AgentToolAdapter {
  readonly id: 'in-memory';
  tools(): readonly AgentTool[];
  invoke(name: string, input?: AgentToolInput): Promise<AgentToolResult>;
}

export function createInMemoryAdapter(): InMemoryAgentToolAdapter {
  const tools = new Map<string, AgentTool>();
  return {
    id: 'in-memory',
    registerTool(tool) {
      const issue = checkAgentTool(tool, tools.keys());
      if (issue) throw new AgentToolRegistrationError(tool.name, issue.code, issue.detail);
      tools.set(tool.name, tool);
    },
    unregisterTool: (name) => void tools.delete(name),
    registeredNames: () => [...tools.keys()],
    tools: () => [...tools.values()],
    async invoke(name, input = {}) {
      const tool = tools.get(name);
      if (!tool) throw new Error(`nenhuma ferramenta ${name} registrada`);
      return tool.execute(input);
    },
  };
}

interface WebMcpModelContext {
  registerTool(tool: {
    name: string;
    description: string;
    inputSchema: unknown;
    annotations: { readOnlyHint: boolean };
    execute(input: AgentToolInput): Promise<AgentToolResult>;
  }): void;
  unregisterTool(name: string): void;
}

export function findWebMcp(target: { isSecureContext?: boolean; navigator?: unknown } = globalThis): WebMcpModelContext | null {
  if (target.isSecureContext === false) return null;
  const nav = target.navigator;
  if (typeof nav !== 'object' || nav === null) return null;
  const mc = (nav as { modelContext?: unknown }).modelContext;
  if (typeof mc !== 'object' || mc === null) return null;
  const { registerTool, unregisterTool } = mc as Partial<WebMcpModelContext>;
  return typeof registerTool === 'function' && typeof unregisterTool === 'function' ? (mc as WebMcpModelContext) : null;
}

function maskedText(text: string): string {
  try {
    return JSON.stringify(maskForAgent(JSON.parse(text)));
  } catch {
    return maskDocuments(text);
  }
}

/** O que sai para agente de fora continua mascarado (U24); a liberação da U56 é só do Luy. */
export function maskToolResult(result: AgentToolResult): AgentToolResult {
  const { structuredContent, ...rest } = result;
  return {
    ...rest,
    content: result.content.map((part) => ({ ...part, text: maskedText(part.text) })),
    ...(structuredContent ? { structuredContent: maskForAgent(structuredContent) as Readonly<Record<string, unknown>> } : {}),
  };
}

export function createWebMcpAdapter(modelContext: WebMcpModelContext): AgentToolAdapter {
  const registered = new Set<string>();
  return {
    id: 'webmcp',
    registerTool(tool) {
      if (tool.internal) return;
      const issue = checkAgentTool(tool, registered);
      if (issue) throw new AgentToolRegistrationError(tool.name, issue.code, issue.detail);
      modelContext.registerTool({
        name: tool.name,
        description: tool.description,
        inputSchema: tool.inputSchema,
        annotations: { readOnlyHint: tool.readOnly },
        execute: async (input) => maskToolResult(await tool.execute(input)),
      });
      registered.add(tool.name);
    },
    unregisterTool(name) {
      if (!registered.delete(name)) return;
      modelContext.unregisterTool(name);
    },
    registeredNames: () => [...registered],
  };
}

export function createFanoutAdapter(adapters: readonly AgentToolAdapter[]): AgentToolAdapter {
  return {
    id: 'in-memory',
    registerTool: (tool) => adapters.forEach((a) => a.registerTool(tool)),
    unregisterTool: (name) => adapters.forEach((a) => a.unregisterTool(name)),
    registeredNames: () => adapters[0]?.registeredNames() ?? [],
  };
}
